import React from 'react';
import { PincodeDetails, PincodeTargetingPayload } from '../../types/pincodeTypes';
import PincodeSearch from './PincodeSearch';
import SelectedPincodes from './SelectedPincodes';

interface PincodeSelectorProps {
  isPincodeSpecific: boolean;
  targetPincodes: PincodeDetails[];
  onTogglePincodeSpecific: (enabled: boolean) => void;
  onPincodeSelectionChange: (pincodes: PincodeDetails[]) => void;
  disabled?: boolean;
  showHelp?: boolean;
  className?: string;
}

const PincodeSelector: React.FC<PincodeSelectorProps> = ({
  isPincodeSpecific,
  targetPincodes,
  onTogglePincodeSpecific,
  onPincodeSelectionChange,
  disabled = false,
  showHelp = true,
  className = ''
}) => {
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    onTogglePincodeSpecific(enabled);
    
    // Clear selected pincodes when disabling
    if (!enabled) {
      onPincodeSelectionChange([]);
    }
  };

  const handlePincodeRemove = (pincodeId: number) => {
    const updatedPincodes = targetPincodes.filter(p => p.pincodeId !== pincodeId);
    onPincodeSelectionChange(updatedPincodes);
  };

  return (
    <div className={`pincode-selector ${className}`}>
      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            id="pincodeSpecificCheckbox"
            className="form-check-input"
            checked={isPincodeSpecific}
            onChange={handleCheckboxChange}
            disabled={disabled}
          />
          <label className="form-check-label fw-bold" htmlFor="pincodeSpecificCheckbox">
            <i className="fas fa-map-marker-alt me-2 text-primary"></i>
            Make this article pincode-specific
          </label>
        </div>
        {showHelp && (
          <small className="text-muted d-block mt-1">
            Check this box to target specific geographic areas for mobile app delivery
          </small>
        )}
      </div>

      {isPincodeSpecific && (
        <div className="pincode-targeting-section border rounded p-3 bg-light">
          <div className="mb-3">
            <label className="form-label">Search and Select Pincodes</label>
            <PincodeSearch
              onPincodeSelect={onPincodeSelectionChange}
              selectedPincodes={targetPincodes}
              disabled={disabled}
              placeholder="Search by pincode, area name, city, or district..."
              showSelectedCount={true}
            />
            
            {showHelp && (
              <small className="text-muted d-block mt-1">
                Type at least 2 characters to search. Click on results to add them to your selection.
              </small>
            )}
          </div>

          {targetPincodes.length > 0 && (
            <div className="mb-3">
              <SelectedPincodes
                pincodes={targetPincodes}
                onRemove={handlePincodeRemove}
                showTitle={true}
                showStats={true}
                className="mt-3"
              />
            </div>
          )}

          {isPincodeSpecific && targetPincodes.length === 0 && (
            <div className="alert alert-info" role="alert">
              <i className="fas fa-info-circle me-2"></i>
              Please select at least one pincode for pincode-specific targeting.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PincodeSelector;
